import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AppInsightsService, genTinyGUID } from '@cudirect/shared-ui-library';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { ReviewComponent } from '@workflow/ecp-workflow/dynamic-app/component/pages/review/review-resolver.component';
import { ReviewBaseComponent } from '@workflow/ecp-workflow/dynamic-app/component/pages/review/review-base.component';
import { MembershipService, Product } from '@workflow/ecp-workflow/dynamic-app/types/accounts-and-services.type';
import { AccountType } from '@workflow/ecp-shared/enum/enum';
import { StringVariableService } from '@workflow/service/string-variable/string-variable.service';
import { WorkflowPageNavigationService } from '@workflow/service/workflow-page-navigation/workflow-page-navigation.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';

/** Share Dividend Type */
const SHARE_DIVIDEND_TYPE = 'ShareDividendType';
/** Share Disbursement Type */
const SHARE_DISBURSEMENT_TYPE = 'ShareDisbursementType';
/** Share Contribution Source */
const SHARE_CONTRIBUTION_SOURCE = 'ShareContributionSource';
/** Shares & Services Picklist Path */
const SHARE_SERVICES_PICKLIST_PATH = 'ss:oao-accounts-and-services-picklist';
/** Const Variable for Accounts & Services Page Id */
const ACCOUNTS_AND_SERVICES = 'oao-accounts-and-services';

/**
 * different type of field layout
 */
@ReviewComponent('accounts-and-service')
@Component({
  selector: 'ecp-oao-accounts-services-review',
  templateUrl: './oao-accounts-services-review.component.html',
  styleUrls: ['./oao-accounts-services-review.component.scss']
})
export class OaoAccountsServiceReviewComponent extends ReviewBaseComponent implements OnInit {
  /** Products and Services */
  public products: Product[] = [];
  /** Membership and Services */
  public membershipServices: MembershipService[] = [];
  /** Included Types */
  public includedSharesTypes = ['checking', 'saving', 'moneymarket', 'other'];
  /** Share Dividend Type Picklist */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public shareDividendTypePicklist: any[];
  /** Share Dividend Type Picklist */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public shareDisbursementTypePicklist: any[];
  /** Share Contribution Source Picklist */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public shareContributionSourceList: any[];
  /** Certificate Shares Types*/
  public certificateSharesTypes = ['sharecertificates', 'retiresharecertificates'];

  constructor(
    public l360Service: L360Service,
    public readonly router: Router,
    public readonly workflowService: WorkflowService,
    private readonly appInsightsService: AppInsightsService,
    public readonly stringVariableService: StringVariableService,
    public readonly workflowPageNavigationService: WorkflowPageNavigationService
  ) {
    super(stringVariableService);
  }
  public ngOnInit(): void {
    this.setAccountsAndServices();
  }

  public setAccountsAndServices(): void {
    const ssAccountsServives = this.statefulService.wrap('ss:oao-accounts-and-services').valueString;
    const ssMembershipServices = this.statefulService.wrap('ss:oao-accounts-membership-services').valueString;

    // Check valid session data available. If so, then populate it, else fetch data from L360 & then load it.
    // When your logout & come back, normally session data will be erased, at this scenorio, we have to load it from backend.
    if (ssAccountsServives || ssMembershipServices) {
      this.LoadShareCertificatePicklist();
      this.getAllProducts();
      this.getAllMembershipServices();
    } else {
      this.LoadShareCertificatePicklist();
      // TODO: We may want to implement AppInsights tracking for ecp.ui.shareEligibility here, like we do in accounts-and-services.c.ts
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.l360Service.GetEligibileProductsFromL360().subscribe((response: any) => {
        if (response.isSuccess) {
          const result = response.data;
          this.l360Service.cachedAccountAndService = response.data;
          const applicantShares = result.primaryApplicant.shares;
          const membershipServices = result.primaryApplicant.membershipServices
            ? result.primaryApplicant.membershipServices.filter(
                (f) => f.relatedProductTypeName.toLowerCase() === 'other' || f.relatedProductTypeName.toLowerCase() === 'atm/debit card'
              )
            : [];
          this.statefulService.wrap('ss:oao-accounts-and-services').value = JSON.stringify(applicantShares);
          this.statefulService.wrap('ss:oao-accounts-membership-services').value = JSON.stringify(membershipServices);
          this.getAllProducts();
          this.getAllMembershipServices();
        }
      });
    }
  }

  public LoadShareCertificatePicklist(): void {
    const picklist = this.statefulService.wrap(SHARE_SERVICES_PICKLIST_PATH).valueString;
    if (picklist) {
      this.LoadPicklistData();
    } else {
      const picklistKeys = `${SHARE_DIVIDEND_TYPE},${SHARE_DISBURSEMENT_TYPE},${SHARE_CONTRIBUTION_SOURCE}`;

      const eventPairId = genTinyGUID(24); // TODO: Replace this with cypto.randomUUID() when TS is updated
      const logSrc = 'workflow';
      this.appInsightsService.logEvent('ecp.ui.picklist', {
        eventState: 'start',
        correlationId: eventPairId,
        source: logSrc,
        keys: picklistKeys
      });

      const picklistTypes = this.l360Service.getPickListTypes(picklistKeys);
      if (picklistTypes?.length > 0) {
        this.statefulService.wrap(SHARE_SERVICES_PICKLIST_PATH).value = JSON.stringify(picklistTypes);

        this.appInsightsService.logEvent('ecp.ui.picklist', {
          eventState: 'end',
          correlationId: eventPairId,
          source: logSrc,
          keys: picklistKeys
        });
      }
      this.LoadPicklistData();
    }
  }

  public LoadPicklistData(): void {
    const picklist = this.statefulService.wrap(SHARE_SERVICES_PICKLIST_PATH).valueString;
    if (picklist) {
      const picklistTypes = JSON.parse(picklist);

      // Check Share Dividend Type
      const shareDividend = picklistTypes.filter((t) => t.value.toLowerCase() === SHARE_DIVIDEND_TYPE.toLowerCase());
      if (shareDividend && shareDividend.length === 1) {
        this.shareDividendTypePicklist = shareDividend[0].pickListItems;
      }

      // Check Share Disbursement Type
      const shareDisbursement = picklistTypes.filter((t) => t.value.toLowerCase() === SHARE_DISBURSEMENT_TYPE.toLowerCase());
      if (shareDisbursement && shareDisbursement.length === 1) {
        this.shareDisbursementTypePicklist = shareDisbursement[0].pickListItems;
      }

      // Check Share Contribution Source
      const shareContribution = picklistTypes.filter((t) => t.value.toLowerCase() === SHARE_CONTRIBUTION_SOURCE.toLowerCase());
      if (shareContribution && shareContribution.length === 1) {
        this.shareContributionSourceList = shareContribution[0].pickListItems;
      }
    }
  }

  // Get Share Dividend Option
  public GetShareDividendName(id: string): string {
    return this.l360Service?.cachedAccountAndService
      ? this.l360Service?.cachedAccountAndService?.shareOptions?.dividendOptions?.pickList.filter((t) => t.id.toString() === id)?.[0]?.label
      : '';
  }

  // Get Share Disbursement Option
  public GetShareDisbursementName(id: string): string {
    return this.l360Service?.cachedAccountAndService
      ? this.l360Service?.cachedAccountAndService?.shareOptions?.maturityOptions?.pickList.filter((t) => t.id.toString() === id)?.[0]?.label
      : '';
  }

  // Get Share Contribution Source Option
  public GetShareContributionName(id: string): string {
    return this.shareContributionSourceList ? this.shareContributionSourceList.filter((f) => f.id.toString() === id)?.[0]?.value : '';
  }

  /** getting all products */
  public getAllProducts(): void {
    this.products = this.statefulService.wrap('ss:oao-accounts-and-services').valueString
      ? JSON.parse(this.statefulService.wrap('ss:oao-accounts-and-services').valueString)
      : [];
  }

  /** getting all MembershipServices */
  public getAllMembershipServices(): void {
    this.membershipServices = this.statefulService.wrap('ss:oao-accounts-membership-services').valueString
      ? JSON.parse(this.statefulService.wrap('ss:oao-accounts-membership-services').valueString)
      : [];
  }

  /** Get only Included Products */
  public get includedProducts(): Product[] {
    if (this.products && this.products.length > 0) {
      return this.products.filter((t) => t.isIncluded && this.includedSharesTypes.indexOf(t.type) > -1);
    }
    return [];
  }
  /** Get only Included Services */
  public get includedServices(): MembershipService[] {
    if (this.membershipServices && this.membershipServices.length > 0) {
      return this.membershipServices.filter((t) => t.isIncluded);
    }
    return [];
  }
  /** Get only selected Products */
  public get selectedProducts(): Product[] {
    if (this.products && this.products.length > 0) {
      return this.products.filter(
        (t) => t.isSelected && !t.isIncluded && (t.terms.some((x) => x.isSelected) || this.certificateSharesTypes.indexOf(t.type) < 0)
      );
    }
    return [];
  }
  /** Get only selected MembershipServices */
  public get selectedMembershipServices(): MembershipService[] {
    if (this.membershipServices && this.membershipServices.length > 0) {
      return this.membershipServices.filter((t) => t.isSelected && !t.isIncluded);
    }
    return [];
  }
  /** Get Total Products and Service */
  public get totalProductsAndService(): number {
    let sum = 0;
    if (this.includedProducts && this.includedProducts.length !== 0) {
      sum = sum + this.includedProducts.length;
    }
    if (this.includedServices && this.includedServices.length !== 0) {
      sum = sum + this.includedServices.length;
    }
    if (this.selectedProducts && this.selectedProducts.length !== 0) {
      sum = sum + this.selectedProducts.length;
    }
    if (this.selectedMembershipServices && this.selectedMembershipServices.length !== 0) {
      sum = sum + this.selectedMembershipServices.length;
    }
    return sum;
  }
  /** Getting Accounts and Services Icon */
  public getIcons(type: string): string {
    switch (type && type.toLowerCase()) {
      case AccountType.Checking:
        return 'assets/images/checking-account-icon.svg';
      case AccountType.SavingsShareAccount:
        return 'assets/images/saving-share-money.svg';
      case AccountType.OnlineBanking:
        return 'assets/images/online-bank.svg';
      case AccountType.OverdraftProtection:
        return 'assets/images/over-draft-protection.svg';
      case AccountType.Saving:
        return 'assets/images/saving-account-icon.svg';
      case AccountType.MoneyMarket:
        return 'assets/images/moneymarket-account-icon.svg';
      case AccountType.Retirement:
        return 'assets/images/retirement-account-icon.svg';
      case AccountType.Other:
      case AccountType.AtmService:
        return 'assets/images/other-account-icon.svg';
      case AccountType.MembershipServices:
        return 'assets/images/membershipservices-account-icon.svg';
      case AccountType.ShareCertificates:
        return 'assets/images/sharecertificates-account-icon.svg';
      case AccountType.RetireShareCertificates:
        return 'assets/images/retiresharecertificates-account-icon.svg';
      default:
        return 'assets/images/checking-account-icon.svg';
    }
  }

  /** get/find Is this the checking account? */
  public isShareCertificateAccount(product: Product): boolean {
    return product.type.toLowerCase() === 'sharecertificates';
  }

  public showIncludedAccountsEdit(): boolean {
    let showEdit = false;
    this.includedProducts.forEach((item) => {
      showEdit = item.optionalServices && item.optionalServices.length > 0 ? true : false;
    });
    return showEdit;
  }

  public redirectToEdit() {
    this.workflowPageNavigationService.navigateToPage(this.page, ACCOUNTS_AND_SERVICES);
  }
}
